import { useQuery } from 'react-query';

import axiosClient from '../../api/axios-client';
import { langueMobile } from '../i18n/langue';

/**
 * Les libellés métier (checklists, préparations, catégories) et les notes de
 * chambre dans la langue de l'app — T1 et P1 du 28/10.
 *
 * Rien à demander en français. Une traduction absente vaut `null` : le serveur la
 * prépare en file, l'écran garde le français et redemande quelques secondes plus
 * tard, au plus quelques fois (une IA en panne ne fait pas tourner l'app en rond).
 */

interface ReponseTraductions {
    locale: string;
    labels: Record<string, string | null>;
    notes: Record<string, string | null>;
}

const RELANCE_MS = 5_000;
const RELANCES_MAX = 6;

export interface TraductionsMetier {
    /** La traduction d'un libellé, ou `null` (français, ou pas encore prête). */
    libelle: (texte: string | null | undefined) => string | null;
    note: (id: number) => string | null;
}

export function useTraductionsMetier(libelles: (string | null | undefined)[], noteIds: number[] = []): TraductionsMetier {
    const locale = langueMobile();
    const textes = [...new Set(libelles.filter((l): l is string => !!l && l.trim() !== ''))].sort();
    const notes = [...new Set(noteIds)].sort((a, b) => a - b);
    const actif = locale !== 'fr' && (textes.length > 0 || notes.length > 0);

    const { data } = useQuery(
        ['hk-label-translations', locale, textes, notes],
        async () => {
            const reponse = await axiosClient.post<ReponseTraductions>('/housekeeping/label-translations', {
                labels: textes,
                note_ids: notes,
            });

            return reponse.data;
        },
        {
            enabled: actif,
            staleTime: 10 * 60_000,
            refetchOnWindowFocus: false,
            refetchInterval: (donnees, requete) => {
                const enAttente =
                    !!donnees &&
                    (Object.values(donnees.labels).some((v) => v === null) ||
                        Object.values(donnees.notes).some((v) => v === null));

                return enAttente && requete.state.dataUpdateCount < RELANCES_MAX ? RELANCE_MS : false;
            },
        },
    );

    return {
        libelle: (texte) => (texte && data?.labels[texte]) || null,
        note: (id) => data?.notes[String(id)] ?? null,
    };
}
